// src/components/inputs/MultipleSelect.jsx

import React, { useMemo, useState } from 'react';
import {
    Box,
    Chip,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
    ListSubheader,
    TextField,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const MultipleSelect = ({
    name,
    selectedValue,
    handleSelectChange,
    multiOptions,
    initialMemberIds = [],
    sx,
}) => {
    const [search, setSearch] = useState('');

    // فلترة الأوبشنز حسب السيرش
    const filteredOptions = useMemo(() => {
        if (!search) return multiOptions || [];
        const lower = search.toLowerCase();
        return (multiOptions || []).filter((opt) =>
            String(opt.name).toLowerCase().includes(lower)
        );
    }, [multiOptions, search]);

    // إزالة أي قيمة من selectedValue عبر الـ Chip
    const handleChipDelete = (valueToDelete) => {
        const newSelected = selectedValue.filter((val) => val !== valueToDelete);
        const syntheticEvent = {
            target: {
                name,
                value: newSelected,
                selectedOptions: newSelected.map((id) => ({ value: id })),
                multiple: true,
            },
        };
        handleSelectChange(syntheticEvent);
    };

    return (
        <Select
            name={name}
            multiple
            value={selectedValue}
            onChange={handleSelectChange}
            input={<OutlinedInput />}
            MenuProps={{
                autoFocus: false,
                PaperProps: { sx: { maxHeight: 320 } },
            }}
            // منع keyboard navigation الافتراضي
            onKeyDown={(e) => {
                // السماح فقط بـ Tab و Escape و Enter
                if (e.key !== 'Tab' && e.key !== 'Escape' && e.key !== 'Enter') {
                    e.stopPropagation();
                }
            }}
            renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(!selected || selected.length === 0) && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ opacity: 0.7 }}
                        >
                            {/* حقل فارغ بدون placeholder نصي ظاهر */}
                        </Typography>
                    )}
                    {selected.map((val) => {
                        const emp = multiOptions.find(
                            (o) => String(o.id) === String(val)
                        );
                        return (
                            <Chip
                                key={val}
                                size="small"
                                variant="outlined"
                                color="primary"
                                label={emp?.name || val}
                                onMouseDown={(e) => e.stopPropagation()}
                                onDelete={() => handleChipDelete(val)}
                                sx={{
                                    maxWidth: 160,
                                    '& .MuiChip-label': {
                                        display: 'block',
                                        // overflow: 'hidden',
                                        // textOverflow: 'ellipsis',
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            )}
            sx={{
                width: '100%',
                mt: 1,
                backgroundColor: (theme) => theme.palette.background.input,
                borderRadius: 1,
                minWidth: '80px',
                minHeight: '40px',
                '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1,
                },
                '& .MuiSelect-icon': {
                    top: '50%',
                    transform: 'translateY(-50%)',
                    right: 8,
                    color: 'text.secondary',
                    fontSize: 20,
                },
                ...sx,
            }}
        >
            {/* حقل السيرش داخل القائمة */}
            <ListSubheader
                sx={{ px: 1, pb: 1, pt: 1 }}
                onKeyDown={(e) => {
                    // منع إغلاق القائمة أو التنقل عند الكتابة
                    e.stopPropagation();
                }}
            >
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        // منع إغلاق القائمة عند الضغط على أي مفتاح
                        e.stopPropagation();
                    }}
                    InputProps={{
                        sx: {
                            fontSize: 13,
                            '& .MuiOutlinedInput-input': { py: 0.6 },
                        },
                    }}
                />
            </ListSubheader>

            {filteredOptions?.length === 0 && (
                <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                        لا توجد نتائج
                    </Typography>
                </MenuItem>
            )}

            {filteredOptions?.map((option) => {
                const isSelected = selectedValue.some(
                    (v) => String(v) === String(option.id)
                );
                return (
                    <MenuItem
                        key={option.id}
                        value={`${option.id}`}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography variant="body2">{option.name}</Typography>
                        {isSelected && (
                            <CheckIcon
                                fontSize="small"
                                color="primary"
                                sx={{ ml: 1, flexShrink: 0 }}
                            />
                        )}
                    </MenuItem>
                );
            })}
        </Select>
    );
};

export default MultipleSelect;
